import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import React, { useState } from "react";
import { useDataProvider, useNotify } from "react-admin";

interface Props {
  selectedIds: number[];
  onComplete?: () => void;
}

const VerifyMultipleBookingDialog: React.FC<Props> = ({
  selectedIds,
  onComplete,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validBookings, setValidBookings] = useState<number[]>([]);
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const handleOpen = async () => {
    setOpen(true);

    // Fetch selected bookings to validate their status
    try {
      const bookingPromises = selectedIds.map((id) =>
        dataProvider.getOne("bookings", { id })
      );
      const bookingResponses = await Promise.all(bookingPromises);
      const fetchedBookings = bookingResponses.map((response) => response.data);

      // Filter only bookings with verifiable statuses
      const verifiableStatuses = ["allocated", "delivered", "report_submitted"];
      const allocated = fetchedBookings.filter((booking) =>
        verifiableStatuses.includes(booking.status)
      );

      setValidBookings(allocated.map((b) => Number(b.id)));
    } catch (error) {
      notify("Error loading booking data", "warning");
    }
  };
  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    if (validBookings.length === 0) {
      notify(
        "No valid bookings to verify (only allocated, delivered, or report_submitted bookings can be verified)",
        "warning"
      );
      return;
    }

    setLoading(true);
    try {
      // Use PUT request to /api/bookings with correct payload format
      const apiUrl = process.env.REACT_APP_API_URL || "/api";
      const token = localStorage.getItem("token");

      const response = await fetch(`${apiUrl}/bookings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ids: validBookings,
          data: {
            action: "verify",
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      notify(`Successfully verified ${validBookings.length} bookings`, "info");
      handleClose();
      onComplete?.();
    } catch (error: any) {
      notify(`Error: ${error.message}`, "warning");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        color="primary"
        onClick={handleOpen}
        size="small"
        disabled={selectedIds.length === 0}
      >
        Verify Delivery ({selectedIds.length})
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="verify-dialog-title"
      >
        <DialogTitle id="verify-dialog-title">
          Verify Multiple Bookings
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {validBookings.length === selectedIds.length ? (
              <>
                Are you sure you want to verify delivery for{" "}
                {selectedIds.length} selected booking(s)? This action cannot be
                undone.
              </>
            ) : (
              <>
                Out of {selectedIds.length} selected bookings, only{" "}
                {validBookings.length} are eligible for verification (allocated,
                delivered, or report_submitted status).
                {validBookings.length === 0 ? (
                  <>
                    <br />
                    <strong>No bookings can be verified.</strong>
                  </>
                ) : (
                  <>
                    <br />
                    Do you want to proceed with verifying {
                      validBookings.length
                    }{" "}
                    booking(s)?
                  </>
                )}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="default">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={loading || validBookings.length === 0}
          >
            {loading
              ? "Verifying..."
              : `Verify${
                  validBookings.length > 0 ? ` (${validBookings.length})` : ""
                }`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VerifyMultipleBookingDialog;
